import { ref } from "vue";

function getFilesFromEvent(event) {
  const fileList = event?.dataTransfer?.files || event?.target?.files || [];
  return Array.from(fileList).filter(Boolean);
}

export function useFileDrop({
  disabled = () => false,
  multiple = false,
  onFileSelected = null,
  onFilesSelected = null,
  resetInputOnChange = true,
} = {}) {
  const fileInput = ref(null);
  const isDropActive = ref(false);

  const isDisabled = () => Boolean(typeof disabled === "function" ? disabled() : disabled);

  const openFilePicker = () => {
    if (isDisabled()) {
      return;
    }

    fileInput.value?.click();
  };

  const setDropActive = (isActive) => {
    if (isDisabled()) {
      return;
    }

    isDropActive.value = Boolean(isActive);
  };

  const selectFiles = async (files) => {
    const selectedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    if (selectedFiles.length === 0 || isDisabled()) {
      return;
    }

    if (typeof onFilesSelected === "function") {
      await onFilesSelected(multiple ? selectedFiles : selectedFiles.slice(0, 1));
      return;
    }

    if (typeof onFileSelected === "function") {
      await onFileSelected(selectedFiles[0]);
    }
  };

  const setInputFiles = (files) => {
    if (!fileInput.value || !files || files.length === 0) {
      return;
    }

    try {
      if (multiple || files.length === 1 || typeof DataTransfer === "undefined") {
        fileInput.value.files = files;
        return;
      }

      const transfer = new DataTransfer();
      transfer.items.add(files[0]);
      fileInput.value.files = transfer.files;
    } catch (_error) {
      // Some older browsers expose FileList as read-only; the selected file is still emitted.
    }
  };

  const handleFileChange = async (event) => {
    const files = getFilesFromEvent(event);
    if (resetInputOnChange && event?.target) {
      event.target.value = "";
    }

    await selectFiles(files);
  };

  const handleDrop = async (event) => {
    isDropActive.value = false;
    setInputFiles(event?.dataTransfer?.files || null);
    await selectFiles(getFilesFromEvent(event));
  };

  return {
    fileInput,
    isDropActive,
    openFilePicker,
    setDropActive,
    handleFileChange,
    handleDrop,
  };
}
